/**
 * Type-safe environment variables
 *
 * All environment-specific values should be accessed through this module.
 * Values are resolved at runtime from window.__NVX_CONFIG__ (injected via
 * public/config.js), falling back to import.meta.env for local development.
 */
import type { NvxRuntimeConfig } from './types/config';

function getConfig<K extends keyof NvxRuntimeConfig>(
  runtimeKey: K,
  viteKey: string,
  defaultValue: string = '',
): string {
  return window.__NVX_CONFIG__?.[runtimeKey] ?? import.meta.env[viteKey] ?? defaultValue;
}

export const env = {
  // ============================================
  // API URLs
  // ============================================

  /** WebAPI base URL (e.g. https://webapi.dev.nvx.ai) */
  get WEBAPI_URL() {
    return getConfig('WEBAPI_URL', 'VITE_WEBAPI_URL');
  },

  // ============================================
  // Auth0
  // ============================================

  /** Auth0 Domain (e.g., auth.dev.nvx.ai) */
  get AUTH0_DOMAIN() {
    return getConfig('AUTH0_DOMAIN', 'VITE_AUTH0_DOMAIN');
  },

  /** Auth0 Client ID */
  get AUTH0_CLIENT_ID() {
    return getConfig('AUTH0_CLIENT_ID', 'VITE_AUTH0_CLIENT_ID');
  },

  /** Auth0 API Audience (e.g., https://api.dev.nvx.ai) */
  get AUTH0_AUDIENCE() {
    return getConfig('AUTH0_AUDIENCE', 'VITE_AUTH0_AUDIENCE');
  },

  // ============================================
  // Third-party Services
  // ============================================

  /** LaunchDarkly Client-side SDK Key */
  get LAUNCHDARKLY_SDK_KEY() {
    return getConfig('LAUNCHDARKLY_SDK_KEY', 'VITE_LAUNCHDARKLY_SDK_KEY');
  },

  /** Application Insights Connection String */
  get APP_INSIGHTS_CONNECTION_STRING() {
    return getConfig('APP_INSIGHTS_CONNECTION_STRING', 'VITE_APP_INSIGHTS_CONNECTION_STRING');
  },

  // ============================================
  // Feature Flags
  // ============================================

  /** Whether this is a production build */
  IS_PRODUCTION: import.meta.env.PROD,

  /** Current environment name */
  get ENVIRONMENT() {
    return getConfig('ENVIRONMENT', 'VITE_ENVIRONMENT', 'development');
  },

  APP_VERSION: import.meta.env.VITE_APP_VERSION || 'local',
  BUILD_NUMBER: import.meta.env.VITE_BUILD_NUMBER || 'local',
  BUILD_ID: import.meta.env.VITE_BUILD_ID || 'local',

  /** @deprecated Use APP_VERSION instead */
  BUILDNUMBER: import.meta.env.VITE_BUILD_NUMBER || 'local',
} as const;

/**
 * Validate that required environment variables are set
 * In development, logs warnings. In production, throws errors.
 */
export function validateEnv(): void {
  const requiredVars = {
    AUTH0_DOMAIN: env.AUTH0_DOMAIN,
    AUTH0_CLIENT_ID: env.AUTH0_CLIENT_ID,
    AUTH0_AUDIENCE: env.AUTH0_AUDIENCE,
    LAUNCHDARKLY_SDK_KEY: env.LAUNCHDARKLY_SDK_KEY,
  };

  const missing = Object.entries(requiredVars)
    .filter(([, value]) => !value)
    .map(([key]) => key);

  if (missing.length > 0) {
    const message = `Missing required environment variables:\n${missing.map((v) => `  - ${v}`).join('\n')}`;

    // In production, throw error. In development, just warn.
    if (env.IS_PRODUCTION) {
      throw new Error(message);
    } else {
      console.warn(`⚠️ ${message}\n\nMake sure .env.local exists and contains all required variables, or config.js is loaded.`);
    }
  }

  // Log warnings for missing optional vars in development
  if (!env.IS_PRODUCTION) {
    const optionalVars = {
      APP_INSIGHTS_CONNECTION_STRING: env.APP_INSIGHTS_CONNECTION_STRING,
      ENVIRONMENT: env.ENVIRONMENT,
    };

    const missingOptional = Object.entries(optionalVars)
      .filter(([, value]) => !value)
      .map(([key]) => key);

    if (missingOptional.length > 0) {
      console.warn(
        `Optional environment variables not set:\n${missingOptional.map((v) => `  - ${v}`).join('\n')}`
      );
    }
  }
}
